import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Main container for the page subheader
 * Provides a flexible layout with composition slots for breadcrumbs, desk dropdown, tabs, and actions
 */
interface PageSubHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Content for the breadcrumbs area (left side)
   */
  breadcrumbs?: React.ReactNode;
  /**
   * Content for the desk dropdown (next to breadcrumbs)
   */
  deskDropdown?: React.ReactNode;
  /**
   * Content for the tabs area (center/below breadcrumbs)
   */
  tabs?: React.ReactNode;
  /**
   * Content for the actions area (right side)
   */
  actions?: React.ReactNode;
  /**
   * Whether to add border bottom to the subheader
   */
  withBorder?: boolean;
  /**
   * Whether to display tabs inline with breadcrumbs (single row) instead of below
   */
  inlineTabs?: boolean;
}

/**
 * PageSubHeader component that provides a flexible layout for breadcrumbs, tabs, and actions
 *
 * @example
 * ```tsx
 * <PageSubHeader
 *   breadcrumbs={
 *     <PageSubHeaderBreadcrumbs>
 *       <Breadcrumb>...</Breadcrumb>
 *     </PageSubHeaderBreadcrumbs>
 *   }
 *   deskDropdown={
 *     <PageSubHeaderDeskDropdown>
 *       <DeskDropdown />
 *     </PageSubHeaderDeskDropdown>
 *   }
 *   tabs={
 *     <PageSubHeaderTabs>
 *       <Tabs>...</Tabs>
 *     </PageSubHeaderTabs>
 *   }
 *   actions={
 *     <PageSubHeaderActions>
 *       <Button>Action</Button>
 *     </PageSubHeaderActions>
 *   }
 * />
 * ```
 */
const PageSubHeader = ({
  className,
  breadcrumbs,
  deskDropdown,
  tabs,
  actions,
  withBorder = true,
  inlineTabs = false,
  ref,
  ...props
}: PageSubHeaderProps & { ref?: React.Ref<HTMLDivElement> }) => {
  if (inlineTabs && tabs) {
    // Single row layout with tabs in the center
    return (
      <div
        ref={ref}
        className={cn(
          'grid h-(--subheader-height) shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center bg-background px-4 lg:px-5 xl:px-6 2xl:px-8',
          withBorder && 'border-b border-border',
          className
        )}
        {...props}
      >
        {/* Left: Breadcrumbs and Desk Dropdown */}
        <div className="flex min-w-0 items-center">
          {breadcrumbs}
          {deskDropdown}
        </div>

        {/* Center: Tabs */}
        <div className="flex min-w-0 justify-center overflow-hidden">{tabs}</div>

        {/* Right: Actions */}
        <div className="flex min-w-0 items-center justify-end space-x-2">{actions}</div>
      </div>
    );
  }

  // Default two-row layout
  return (
    <div
      ref={ref}
      className={cn(
        'flex h-(--subheader-height) shrink-0 flex-col justify-center space-y-2 bg-background px-4 lg:px-5 xl:px-6 2xl:px-8',
        withBorder && 'border-b border-border',
        className
      )}
      {...props}
    >
      {/* Top row: Breadcrumbs, Desk Dropdown, and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center">
          {breadcrumbs}
          {deskDropdown}
        </div>
        {actions && <div className="ml-4 flex items-center space-x-2">{actions}</div>}
      </div>

      {/* Bottom row: Tabs (if provided) */}
      {tabs && <div className="flex items-center">{tabs}</div>}
    </div>
  );
};

/**
 * Container for breadcrumbs content
 * Provides consistent styling and spacing for breadcrumb navigation
 */
interface PageSubHeaderBreadcrumbsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Breadcrumbs container component for the PageSubHeader
 */
const PageSubHeaderBreadcrumbs = ({
  className,
  children,
  ref,
  ...props
}: PageSubHeaderBreadcrumbsProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div ref={ref} className={cn('flex min-w-0 items-center', className)} {...props}>
      {children}
    </div>
  );
};

/**
 * Container for desk dropdown content
 * Provides consistent styling and spacing for desk dropdown
 */
interface PageSubHeaderDeskDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Desk dropdown container component for the PageSubHeader
 */
const PageSubHeaderDeskDropdown = ({
  className,
  children,
  ref,
  ...props
}: PageSubHeaderDeskDropdownProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div ref={ref} className={cn('flex items-center', className)} {...props}>
      {children}
    </div>
  );
};

/**
 * Container for tabs content
 * Provides consistent styling for tab navigation
 */
interface PageSubHeaderTabsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Tabs container component for the PageSubHeader
 */
const PageSubHeaderTabs = ({
  className,
  children,
  ref,
  ...props
}: PageSubHeaderTabsProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div ref={ref} className={cn('flex items-center', className)} {...props}>
      {children}
    </div>
  );
};

/**
 * Container for actions content
 * Provides consistent styling and spacing for action buttons
 */
interface PageSubHeaderActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Actions container component for the PageSubHeader
 */
const PageSubHeaderActions = ({
  className,
  children,
  ref,
  ...props
}: PageSubHeaderActionsProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div ref={ref} className={cn('flex items-center space-x-2', className)} {...props}>
      {children}
    </div>
  );
};

export {
  PageSubHeader,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderDeskDropdown,
  PageSubHeaderTabs,
  PageSubHeaderActions,
  type PageSubHeaderProps,
  type PageSubHeaderBreadcrumbsProps,
  type PageSubHeaderDeskDropdownProps,
  type PageSubHeaderTabsProps,
  type PageSubHeaderActionsProps,
};
